/**
 * Shared authenticated API client for the AETHER frontend.
 *
 * Centralizes the bearer-token transport so every service (events, SkyWrench,
 * ...) talks to the beta API the same way: attach a fresh Cognito ID token,
 * refresh once on a 401, and redirect to login at most once per page load.
 * Keeping this in one module avoids duplicating the auth/refresh logic per
 * service and gives us a single place to evolve the transport.
 */
import { createLogger } from '../utils/logger';
import { ensureFreshToken, refreshTokens, getIdToken, redirectToLogin } from '../auth';

const logger = createLogger('ApiClient');

/**
 * Base URL of the Cognito-protected beta API. Overridable via the build-time
 * REACT_APP_API_URL so non-production environments can target their own stack.
 *
 * The fallback is the **v2** HTTP API (stack `aether-beta-events-api-v2`), which
 * is what `.env` configures and what this frontend is built against. It used to
 * be the legacy v1 endpoint (`s3m6s2zgoi`), meaning an unset REACT_APP_API_URL
 * silently pointed the app at the superseded single-table API instead of failing
 * or degrading visibly.
 */
export const API_URL: string =
  process.env.REACT_APP_API_URL || 'https://9bvutogq83.execute-api.us-east-1.amazonaws.com/beta';

const CONFIGURED_ERTS_INFERENCE_API_URL: string =
  process.env.REACT_APP_ERTS_INFERENCE_API_URL || '';

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

const AUTHORIZED_API_BASE_URLS: readonly string[] = [
  API_URL,
  ...(CONFIGURED_ERTS_INFERENCE_API_URL ? [CONFIGURED_ERTS_INFERENCE_API_URL] : []),
].map(normalizeBaseUrl);

function authorizedBaseUrl(baseUrl: string): string {
  const normalizedBaseUrl: string = normalizeBaseUrl(baseUrl);
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(normalizedBaseUrl);
  } catch {
    throw new Error('Authenticated API base URL is invalid');
  }
  if (parsedUrl.protocol !== 'https:' || !AUTHORIZED_API_BASE_URLS.includes(normalizedBaseUrl)) {
    throw new Error('Authenticated API base URL is not authorized');
  }
  return normalizedBaseUrl;
}

/**
 * Guards against an infinite login loop. Once we have redirected to login due
 * to an unrecoverable 401, we do not redirect again for the lifetime of this
 * page load — otherwise a user whose token is valid for Cognito but rejected by
 * the API authorizer would bounce between the app and the IdP forever. The flag
 * resets naturally on the next full page load (after a real re-auth). It is
 * shared across all services so a single redirect covers the whole app.
 */
let loginRedirectIssued = false;

/**
 * Options accepted by {@link authedFetch}.
 */
export interface AuthedFetchInit {
  method?: string;
  body?: string;
  includeContentType?: boolean;
  baseUrl?: string;
  signal?: AbortSignal;
  authorizationFormat?: 'bearer' | 'raw';
  redirectOnUnauthorized?: boolean;
}

/**
 * Builds request headers, attaching the supplied ID token when present.
 */
function buildHeaders(
  token: string | null,
  includeContentType = false,
  authorizationFormat: 'bearer' | 'raw' = 'bearer',
): HeadersInit {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = authorizationFormat === 'bearer' ? `Bearer ${token}` : token;
  }
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

/**
 * Performs an authenticated fetch against the beta API with automatic token
 * refresh. Ensures a fresh ID token before the call; on a 401 (token rejected)
 * it refreshes once and retries. If the session cannot be renewed, it redirects
 * to login (at most once per page load) and resolves to null.
 *
 * @param path API path relative to the selected base URL, e.g. `/events`.
 * @param init Request method, body, authentication format, and transport options.
 * @returns The fetch Response, or null when the request could not be authenticated.
 */
export async function authedFetch(
  path: string,
  init: AuthedFetchInit = {},
): Promise<Response | null> {
  const {
    method = 'GET',
    body,
    includeContentType = false,
    baseUrl = API_URL,
    signal,
    authorizationFormat = 'bearer',
    redirectOnUnauthorized = true,
  } = init;

  try {
    const normalizedBaseUrl: string = authorizedBaseUrl(baseUrl);
    const doFetch = (token: string | null): Promise<Response> =>
      fetch(`${normalizedBaseUrl}${path}`, {
        method,
        body,
        signal,
        headers: buildHeaders(token, includeContentType, authorizationFormat),
      });
    const token = await ensureFreshToken();
    let response = await doFetch(token);

    // Token rejected mid-flight: refresh once and retry before giving up.
    if (response.status === 401) {
      const refreshed = await refreshTokens();
      if (refreshed) {
        response = await doFetch(getIdToken());
      }
      if (response.status === 401) {
        if (!redirectOnUnauthorized) {
          logger.warn('Authorization rejected by optional API', { path });
          return null;
        }
        // Redirect to login at most once per page load to avoid an infinite
        // app -> IdP -> app loop when the session cannot be made valid.
        if (!loginRedirectIssued) {
          loginRedirectIssued = true;
          logger.warn('Session expired and could not be refreshed; redirecting to login');
          redirectToLogin();
        } else {
          logger.error(
            'Authorization repeatedly rejected; not redirecting again to avoid a login loop',
          );
        }
        return null;
      }
    }
    return response;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('authedFetch failed', { path, error: message });
    return null;
  }
}
